<template>
  <main class="adm-page">

    <div class="adm-header">
      <div>
        <h1 class="adm-header__title">Gestion des Formations</h1>
        <p class="adm-header__sub">{{ formationsPagination.total }} formations au total</p>
      </div>
      <div class="adm-header__actions">
        <button class="adm-btn adm-btn--terra" @click="openNewFormationDialog"><Plus />Nouvelle formation</button>
      </div>
    </div>

    <!-- KPIs -->
    <div class="adm-kpis">
      <div class="adm-kpi adm-kpi--forest">
        <div class="adm-kpi__icon"><BookOpen /></div>
        <span class="adm-kpi__val">{{ formationStats.total || 0 }}</span>
        <span class="adm-kpi__lbl">Total formations</span>
      </div>
      <div class="adm-kpi adm-kpi--sage">
        <div class="adm-kpi__icon"><GraduationCap /></div>
        <span class="adm-kpi__val">{{ formationStats.active || 0 }}</span>
        <span class="adm-kpi__lbl">Actives</span>
      </div>
      <div class="adm-kpi adm-kpi--amber">
        <div class="adm-kpi__icon"><Users /></div>
        <span class="adm-kpi__val">{{ formationStats.total_enrolled || 0 }}</span>
        <span class="adm-kpi__lbl">Inscrits</span>
      </div>
      <div class="adm-kpi adm-kpi--terra">
        <div class="adm-kpi__icon"><Award /></div>
        <span class="adm-kpi__val">{{ formationStats.average_success_rate || 95 }}%</span>
        <span class="adm-kpi__lbl">Taux de réussite</span>
      </div>
    </div>

    <!-- Filtres -->
    <div class="adm-filters">
      <div class="adm-search" style="flex:1;min-width:200px">
        <Search /><input v-model="filters.search" @input="debouncedSearch" placeholder="Rechercher…" class="adm-input" />
      </div>
      <select v-model="filters.level" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Tous niveaux</option>
        <option value="Débutant">Débutant</option>
        <option value="Intermédiaire">Intermédiaire</option>
        <option value="Avancé">Avancé</option>
      </select>
      <select v-model="filters.status" @change="applyFilters" class="adm-input adm-input--sm">
        <option value="">Tous statuts</option>
        <option value="upcoming">À venir</option>
        <option value="active">Active</option>
        <option value="complet">Complète</option>
        <option value="closed">Fermée</option>
      </select>
      <button class="adm-btn adm-btn--outline adm-btn--sm" @click="resetFilters"><X />Réinitialiser</button>
    </div>

    <!-- Table -->
    <div class="adm-card">
      <div v-if="loading" class="adm-loading"><span class="adm-ring"></span></div>
      <div v-else>
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead>
              <tr>
                <th>Formation</th>
                <th>Niveau</th>
                <th>Durée</th>
                <th>Capacité</th>
                <th>Prochaine session</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!formations.length">
                <td colspan="7" class="adm-table__empty">Aucune formation trouvée</td>
              </tr>
              <tr v-for="f in formations" :key="f.id">
                <td data-label="Formation">
                  <div class="adm-cell-title">{{ f.title }}</div>
                  <div class="adm-cell-sub">{{ f.trainer?.name || f.price || '—' }}</div>
                </td>
                <td data-label="Niveau"><span class="adm-chip">{{ f.level }}</span></td>
                <td data-label="Durée">{{ f.duration }}</td>
                <td data-label="Capacité">
                  <div style="display:flex;flex-direction:column;gap:.25rem">
                    <span class="adm-cell-num">{{ f.enrolled || 0 }}/{{ f.capacity }}</span>
                    <div class="adm-progress-track" style="width:80px">
                      <div class="adm-progress-fill" :style="{width: f.capacity ? Math.min(100,(f.enrolled||0)/f.capacity*100)+'%' : '0%'}"></div>
                    </div>
                  </div>
                </td>
                <td data-label="Session" class="adm-cell-date">{{ fmtDate(f.next_session) }}</td>
                <td data-label="Statut">
                  <span class="adm-badge" :class="{
                    'adm-badge--sage':  f.status==='active',
                    'adm-badge--amber': f.status==='upcoming',
                    'adm-badge--gray':  f.status==='complet'||f.status==='closed'
                  }">{{ statusLabel(f.status) }}</span>
                </td>
                <td class="no-label">
                  <div class="adm-actions">
                    <button class="adm-btn adm-btn--outline adm-btn--xs" @click="viewRegistrations(f)" title="Inscriptions"><Users /></button>
                    <div class="adm-drop">
                      <button class="adm-btn adm-btn--outline adm-btn--xs" @click.stop="toggleActionsMenu(f.id)"><MoreVertical /></button>
                      <div v-if="activeActionMenu===f.id" class="adm-drop__menu">
                        <button class="adm-drop__item" @click="editFormation(f)"><Edit />Modifier</button>
                        <button class="adm-drop__item adm-drop__item--red" @click="confirmDelete(f)"><Trash2 />Supprimer</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="formationsPagination.last_page > 1" class="adm-pagination">
          <p class="adm-pagination__info">{{ formationsPagination.total }} formations</p>
          <div class="adm-pagination__pages">
            <button class="adm-page-btn" :disabled="formationsPagination.current_page===1" @click="changePage(formationsPagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="adm-page-btn" :class="{'adm-page-btn--active':formationsPagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="adm-page-btn" :disabled="formationsPagination.current_page===formationsPagination.last_page" @click="changePage(formationsPagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL : Formation ══════════════════════════ -->
    <Transition name="adm-modal">
      <div v-if="showDialog" class="adm-modal" @click.self="closeDialog">
        <div class="adm-modal__box adm-modal__box--lg">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">{{ editingFormation ? 'Modifier' : 'Nouvelle formation' }}</h3>
            <button class="adm-modal__close" @click="closeDialog"><X /></button>
          </div>
          <form @submit.prevent="submitForm" class="adm-modal__body">
            <div class="adm-field"><label class="adm-label">Titre <span>*</span></label><input v-model="formationForm.title" required class="adm-input" /></div>
            <div class="adm-form-grid">
              <div class="adm-field"><label class="adm-label">Niveau <span>*</span></label>
                <select v-model="formationForm.level" required class="adm-input">
                  <option value="Débutant">Débutant</option><option value="Intermédiaire">Intermédiaire</option><option value="Avancé">Avancé</option>
                </select>
              </div>
              <div class="adm-field"><label class="adm-label">Statut</label>
                <select v-model="formationForm.status" class="adm-input">
                  <option value="upcoming">À venir</option><option value="active">Active</option><option value="complet">Complète</option><option value="closed">Fermée</option>
                </select>
              </div>
              <div class="adm-field"><label class="adm-label">Durée <span>*</span></label><input v-model="formationForm.duration" required class="adm-input" placeholder="ex: 4 semaines" /></div>
              <div class="adm-field"><label class="adm-label">Horaires</label><input v-model="formationForm.schedule" class="adm-input" placeholder="Lun-Ven 9h-12h" /></div>
              <div class="adm-field"><label class="adm-label">Capacité <span>*</span></label><input v-model.number="formationForm.capacity" required type="number" min="1" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Prochaine session</label><input v-model="formationForm.next_session" type="date" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Prix</label><input v-model="formationForm.price" class="adm-input" placeholder="Gratuit" /></div>
              <div class="adm-field"><label class="adm-label">Taux réussite (%)</label><input v-model.number="formationForm.success_rate" type="number" min="0" max="100" class="adm-input" /></div>
            </div>
            <div class="adm-field"><label class="adm-label">Description</label><textarea v-model="formationForm.description" rows="3" class="adm-input adm-textarea"></textarea></div>
            <div style="border-top:1px solid var(--ink-12);padding-top:1rem">
              <p class="adm-section-title">Formateur</p>
              <div class="adm-form-grid">
                <div class="adm-field"><label class="adm-label">Nom</label><input v-model="formationForm.trainer.name" class="adm-input" /></div>
                <div class="adm-field"><label class="adm-label">Titre</label><input v-model="formationForm.trainer.title" class="adm-input" /></div>
                <div class="adm-field" style="grid-column:span 2"><label class="adm-label">Expérience</label><input v-model="formationForm.trainer.experience" class="adm-input" /></div>
              </div>
            </div>
          </form>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="closeDialog">Annuler</button>
            <button class="adm-btn adm-btn--terra" :disabled="loading" @click="submitForm">
              <span v-if="loading" class="adm-ring adm-ring--sm"></span>
              {{ editingFormation ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete confirm -->
    <Transition name="adm-modal">
      <div v-if="showDeleteConfirm" class="adm-modal" @click.self="showDeleteConfirm=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head"><h3 class="adm-modal__title">Supprimer la formation ?</h3><button class="adm-modal__close" @click="showDeleteConfirm=false"><X /></button></div>
          <div class="adm-modal__body"><div class="adm-confirm"><div class="adm-confirm__icon"><Trash2 /></div><h3>Confirmer</h3><p>« {{ formationToDelete?.title }} » et toutes ses inscriptions seront supprimées.</p></div></div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showDeleteConfirm=false">Annuler</button>
            <button class="adm-btn adm-btn--red" @click="deleteFormationConfirmed"><Trash2 />Supprimer</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="adm-toast"><div v-if="toastMessage" class="adm-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { Plus, Search, X, ChevronLeft, ChevronRight, MoreVertical, Eye, Edit, Trash2,
  CheckCircle, GraduationCap, Calendar, Users, TrendingUp, Award, BookOpen } from 'lucide-vue-next'

export default {
  name: 'AdminFormations',
  components: { Plus, Search, X, ChevronLeft, ChevronRight, MoreVertical, Eye, Edit, Trash2, CheckCircle, GraduationCap, Calendar, Users, TrendingUp, Award, BookOpen },
  data() {
    return {
      activeActionMenu:null, showDialog:false, showDeleteConfirm:false,
      editingFormation:null, formationToDelete:null, displayedPages:[], toastMessage:'', newSkill:'',
      filters:{search:'',level:'',status:'',sort_by:'created_at',sort_order:'desc'},
      formationForm:{id:null,title:'',slug:'',description:'',duration:'',level:'Débutant',schedule:'',capacity:20,enrolled:0,status:'upcoming',objectives:[],skills:[],next_session:'',price:'Gratuit',icon:'Code',trainer:{name:'',title:'',experience:''},success_rate:95,graduates_count:0}
    }
  },
  computed: {
    ...mapState({loading:s=>s.loading,formations:s=>s.formations,formationsPagination:s=>s.formationsPagination,formationStats:s=>s.formationStats})
  },
  watch: {
    'formationsPagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},
    'formationsPagination.current_page':{handler(){this.updateDisplayedPages()}},
    toastMessage:{handler(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}}
  },
  created(){this.debouncedSearch=debounce(this.applyFilters,500);this.loadData() },
  methods: {
    ...mapActions(['fetchFormations','fetchFormationStats','saveFormation','deleteFormation','setFormationFilter','setFormationPage']),
    async loadData(){await Promise.all([this.fetchFormations(),this.fetchFormationStats()])},
    openNewFormationDialog(){this.editingFormation=null;this.resetFormationForm();this.showDialog=true},
    editFormation(f){this.editingFormation=f;this.formationForm={id:f.id,title:f.title||'',slug:f.slug||'',description:f.description||'',duration:f.duration||'',level:f.level||'Débutant',schedule:f.schedule||'',capacity:f.capacity||20,enrolled:f.enrolled||0,status:f.status||'upcoming',objectives:f.objectives||[],skills:f.skills||[],next_session:f.next_session?f.next_session.slice(0,10):'',price:f.price||'Gratuit',icon:f.icon||'Code',trainer:f.trainer||{name:'',title:'',experience:''},success_rate:f.success_rate||95,graduates_count:f.graduates_count||0};this.showDialog=true;this.activeActionMenu=null},
    closeDialog(){this.showDialog=false;this.editingFormation=null;this.resetFormationForm()},
    resetFormationForm(){this.formationForm={id:null,title:'',slug:'',description:'',duration:'',level:'Débutant',schedule:'',capacity:20,enrolled:0,status:'upcoming',objectives:[],skills:[],next_session:'',price:'Gratuit',icon:'Code',trainer:{name:'',title:'',experience:''},success_rate:95,graduates_count:0}},
    async submitForm(){try{const fd=new FormData();if(this.formationForm.id)fd.append('id',this.formationForm.id);['title','description','level','duration','schedule','status','price','icon'].forEach(k=>{if(this.formationForm[k])fd.append(k,this.formationForm[k])});fd.append('capacity',this.formationForm.capacity);fd.append('success_rate',this.formationForm.success_rate);if(this.formationForm.next_session)fd.append('next_session',this.formationForm.next_session);fd.append('trainer',JSON.stringify(this.formationForm.trainer));fd.append('objectives',JSON.stringify(this.formationForm.objectives));fd.append('skills',JSON.stringify(this.formationForm.skills));await this.saveFormation(fd);this.closeDialog();this.loadData();this.showToast(this.formationForm.id?'Formation modifiée':'Formation créée')}catch(e){console.error(e)}},
    confirmDelete(f){this.formationToDelete=f;this.showDeleteConfirm=true;this.activeActionMenu=null},
    async deleteFormationConfirmed(){if(this.formationToDelete){try{await this.deleteFormation(this.formationToDelete.id);this.showDeleteConfirm=false;this.formationToDelete=null;this.loadData();this.showToast('Formation supprimée')}catch(e){console.error(e)}}},
    viewRegistrations(f){this.$router.push({name:'single-admin-formations',params:{id:f.id}})},
    applyFilters(){this.setFormationFilter({key:'search',value:this.filters.search});this.setFormationFilter({key:'level',value:this.filters.level});this.setFormationFilter({key:'status',value:this.filters.status});this.fetchFormations()},
    sortBy(field){let order=this.filters.sort_by===field?(this.filters.sort_order==='asc'?'desc':'asc'):'asc';this.filters.sort_by=field;this.filters.sort_order=order;this.applyFilters()},
    resetFilters(){this.filters={search:'',level:'',status:'',sort_by:'created_at',sort_order:'desc'};this.applyFilters()},
    changePage(p){if(p>=1&&p<=this.formationsPagination.last_page)this.setFormationPage(p)},
    updateDisplayedPages(){const c=this.formationsPagination.current_page,l=this.formationsPagination.last_page,d=2,pages=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pages.push(i)}this.displayedPages=pages},
    toggleActionsMenu(id){this.activeActionMenu=this.activeActionMenu===id?null:id},
    fmtDate(d){if(!d)return'—';return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'})},
    statusLabel(s){return{upcoming:'À venir',active:'Active',complet:'Complète',closed:'Fermée'}[s]||s},
    showToast(m){this.toastMessage=m}
  },
  mounted(){document.addEventListener('click',e=>{if(!e.target.closest('.adm-drop')){this.activeActionMenu=null}})}
}
</script>
<style>@import './admin.css';</style>
